import { uniq, isEqual, getUid } from "@/utils";
import simulationOptions from "../models/simulationOptions";
import { getDefaultsSnow } from "../models/simulationDefaults";

export default function toFrontend(data) {
	const { request } = data;
	const useConsole = request.hasRightCantilever;

	const unmergeSpanValues = createUnmergeSpanValues(useConsole);
	const convertPointLoads = createConvertPointLoads(useConsole);

	const spans = {
		useConsole,
		...unmergeSpanValues({
			values: request.spanLengthsM,
			map: (v) => ({
				size: v,
				id: getUid(),
			}),
		}),
	};

	const values = {
		type: request.simulationType,
		location: request.location,
		angle: request.inclinationDegrees,
		spans,
		loads: {
			permanent: {
				distributed: convertLoadPermamentDistributed(
					{
						loadPermanentDistributedMin: request.loadPermanentDistributedMin,
						loadPermanentDistributedMax: request.loadPermanentDistributedMax,
						loadPermanentDistributed: request.loadPermanentDistributed,
					},
					{
						unmergeSpanValues,
					}
				),
				point: convertPointLoads(request.loadPermanentPoint),
			},
			live: {
				distributed: convertLoadLiveDistributed(
					{
						loadLiveDistributed: request.loadLiveDistributed,
						loadLiveCombinationFactor: request.loadLiveCombinationFactor,
					},
					{
						unmergeSpanValues,
					}
				),
				point: convertPointLoads(request.loadLivePoint),
			},
		},
		wind: {
			deltas: [
				request.loadWindCombinationFactor.psi1,
				request.loadWindCombinationFactor.psi2,
				request.loadWindCombinationFactor.psi3,
			],
			cfgMode: {
				isBandByBand: (() => {
					[
						request.loadWindPressure,
						request.loadWindSuction,
						request.loadWindLocalPressure,
						request.loadWindLocalSuction,
					].some((load) => {
						return Object.values(load).some((v) => v !== 0);
					});
				})(),
				deltaCfgOption:
					simulationOptions.windDeltasCfg.options.find(({ deltas }) => {
						return (
							request.loadWindCombinationFactor.psi1 === deltas[0] &&
							request.loadWindCombinationFactor.psi2 === deltas[1] &&
							request.loadWindCombinationFactor.psi3 === deltas[2]
						);
					})?.id ?? "customized",
			},
			general: {
				pressure: {
					w: [
						request.loadWindPressure.w1,
						request.loadWindPressure.w2,
						request.loadWindPressure.w3,
					],
					l: [
						request.loadWindPressure.l1,
						request.loadWindPressure.l2,
						request.loadWindPressure.l3,
					],
				},
				suction: {
					w: [
						request.loadWindSuction.w1,
						request.loadWindSuction.w2,
						request.loadWindSuction.w3,
					],
					l: [
						request.loadWindSuction.l1,
						request.loadWindSuction.l2,
						request.loadWindSuction.l3,
					],
				},
			},
			local: {
				use: [request.loadWindLocalPressure, request.loadWindLocalSuction].some(
					(load) => {
						return Object.values(load).some((v) => v !== 0);
					}
				),
				pressure: {
					w: [
						request.loadWindLocalPressure.w1,
						request.loadWindLocalPressure.w2,
						request.loadWindLocalPressure.w3,
					],
					l: [
						request.loadWindLocalPressure.l1,
						request.loadWindLocalPressure.l2,
						request.loadWindLocalPressure.l3,
					],
				},
				suction: {
					w: [
						request.loadWindLocalSuction.w1,
						request.loadWindLocalSuction.w2,
						request.loadWindLocalSuction.w3,
					],
					l: [
						request.loadWindLocalSuction.l1,
						request.loadWindLocalSuction.l2,
						request.loadWindLocalSuction.l3,
					],
				},
			},
		},
		snow: {
			deltas: [
				request.loadSnowCombinationFactor.psi1,
				request.loadSnowCombinationFactor.psi2,
				request.loadSnowCombinationFactor.psi3,
			],
			cfgMode: {
				deltaCfgOption:
					simulationOptions.snowDeltasCfg.options.find(({ deltas }) => {
						return (
							request.loadSnowCombinationFactor.psi1 === deltas[0] &&
							request.loadSnowCombinationFactor.psi2 === deltas[1] &&
							request.loadSnowCombinationFactor.psi3 === deltas[2]
						);
					})?.id ?? "customized",
			},
			general: {
				cfgMode: {
					isSpanBySpan: uniq(request.loadSnowRegular).length !== 1,
				},
				...unmergeSpanValues({
					values: request.loadSnowRegular,
				}),
			},
			exceptional: (() => {
				if (!request.loadSnowExceptional) {
					return getDefaultsSnow({ spans }).exceptional;
				}

				return {
					use: true,
					cfgMode: {
						isSpanBySpan: uniq(request.loadSnowExceptional).length !== 1,
					},
					...unmergeSpanValues({
						values: request.loadSnowExceptional,
					}),
				};
			})(),
			snowAccumulation: {
				use: [
					request.loadSnowAccumulation.w1,
					request.loadSnowAccumulation.w2,
					request.loadSnowAccumulation.w3,
					request.loadSnowAccumulation.l1,
					request.loadSnowAccumulation.l2,
					request.loadSnowAccumulation.l3,
				].some((v) => v !== 0),
				s: [
					request.loadSnowAccumulation.w1,
					request.loadSnowAccumulation.w2,
					request.loadSnowAccumulation.w3,
				],
				l: [
					request.loadSnowAccumulation.l1,
					request.loadSnowAccumulation.l2,
					request.loadSnowAccumulation.l3,
				],
			},
		},

		axialLoads: {
			use: request.axialLoad.valueKn !== 0,
			value: request.axialLoad.valueKn,
			direction: request.axialLoad.direction,
			type: request.axialLoad.type,
		},

		locks: convertSpanRestraintsIntoLocks(
			{
				spanRestraints: request.spanRestraints,
			},
			{ unmergeSpanValues }
		),

		profileType: request.sectionTypes[0],
		profileFormat: request.sectionOrientation,
		profileOrientation: request.profileOrientation,

		profileSpacing: request.transverseLengthM,
		supportWidth: request.supportWidth,

		claddingProperties: {
			use: [
				request.claddingProperties.coveringInertia,
				request.claddingProperties.fixationsPerMeter,
			].some((v) => v !== 0),
			coveringInertia: request.claddingProperties.coveringInertia,
			fixationsPerMeter: request.claddingProperties.fixationsPerMeter,
		},

		deformation: request.deflectionLimit.limit,
		combination: request.deflectionLimit.combination,
	};

	return {
		id: data.id,
		values,
		project: request.metadata.projectName,
		name: data.name,
		chosenSolution: data.chosenResultId,
		steelGrades: request.steelGrades,
		creationDate: new Date(data.creationDate),
		lastUpdateDate: new Date(data.lastUpdateDate),
		calculationStatus: data.calculationStatus,
		result: data.result,
	};
}

const convertSpanRestraintsIntoLocks = (
	{ spanRestraints },
	{ unmergeSpanValues }
) => {
	const { lower, upper } = spanRestraints;

	const upperFibre = {
		type: uniq(upper).length === 1 && upper[0] === 0 ? 0 : 1,
		lateralLocks: {
			...unmergeSpanValues({
				values: upper,
			}),
			cfgMode: {
				isSpanBySpan: uniq(upper).length !== 1,
			},
		},
	};
	const lowerFibre = {
		use: uniq(lower).length === 1 && lower[0] === 0,
		lateralLocks: {
			...unmergeSpanValues({
				values: lower,
			}),
			cfgMode: {
				isSpanBySpan: uniq(lower).length !== 1,
			},
		},
	};

	const use = upperFibre.type !== 0 || lowerFibre.use;

	return {
		use,
		upperFibre,
		lowerFibre,
	};
};

const convertLoadLiveDistributed = (
	{ loadLiveDistributed, loadLiveCombinationFactor },
	{ unmergeSpanValues }
) => {
	const cfgMode = {
		isSpanBySpan: uniq(loadLiveDistributed).length !== 1,
		deltaCfgOption:
			simulationOptions.liveDistributedDeltaCfg.options.find(({ deltas }) => {
				return (
					loadLiveCombinationFactor.psi1 === deltas[0] &&
					loadLiveCombinationFactor.psi2 === deltas[1] &&
					loadLiveCombinationFactor.psi3 === deltas[2]
				);
			})?.id ?? "customized",
	};

	const { main, console } = unmergeSpanValues({
		values: loadLiveDistributed,
	});

	const deltas = [
		loadLiveCombinationFactor.psi1,
		loadLiveCombinationFactor.psi2,
		loadLiveCombinationFactor.psi3,
	];

	return {
		cfgMode,
		deltas,
		main,
		console,
	};
};

const convertLoadPermamentDistributed = (
	{
		loadPermanentDistributedMin,
		loadPermanentDistributedMax,
		loadPermanentDistributed,
	},
	{ unmergeSpanValues }
) => {
	const cfgMode = {};

	let minMaxMerged;

	if (loadPermanentDistributedMin?.length) {
		cfgMode.isMinMax = true;

		minMaxMerged = loadPermanentDistributedMin.map((v, i) => {
			return {
				min: v,
				max: loadPermanentDistributedMax[i],
			};
		});
	} else {
		cfgMode.isMinMax = false;
		minMaxMerged = loadPermanentDistributed.map((v) => {
			return {
				min: v,
				max: v,
			};
		});
	}

	cfgMode.isSpanBySpan =
		uniq(minMaxMerged.map((v) => v.min)).length !== 1 ||
		uniq(minMaxMerged.map((v) => v.max)).length !== 1;

	const { main, console } = unmergeSpanValues({
		values: minMaxMerged,
	});

	return {
		cfgMode,
		main,
		console,
	};
};

// helpers
function getSpanId({ idx, loads, useConsole }) {
	if (useConsole) {
		if (idx === 0) {
			return "console:0";
		} else if (idx === loads.length - 1) {
			return "console:1";
		} else {
			return `span:${idx - 1}`;
		}
	}

	return `span:${idx}`;
}

function createUnmergeSpanValues(useConsole) {
	return function unmergeSpanValues({ values, map = (v) => v }) {
		if (useConsole) {
			return {
				main: values.slice(1, -1).map((v) => map(v)),
				console: [values[0], values.slice(-1)[0]].map((v) => map(v)),
			};
		} else {
			return {
				main: values.map((v) => map(v)),
				console: [],
			};
		}
	};
}

function createConvertPointLoads(useConsole) {
	return function convertPointLoads(loads) {
		// loads = [
		//     // console span 0 or main span 0 depending on useConsole
		//     {
		//         p1,
		//         p2,
		//         l1,
		//         l2
		//     },
		//     // same for following spans, considering main span 0 follows console span 0,
		//     // and console span 1 follows last span
		//     // ...
		// ]

		// we want to convert it to this:
		// [
		//     {
		//         targetSpans: ["span:0"], // or ["console:0"]
		//         loads: [p1, p2, l1, l2]
		//     },
		//     {
		//         targetSpans: ["span:1", "span:4"],
		//         loads: [p1, p2, l1, l2]
		//     },
		//      ...
		//]

		const emptyLoad = {
			p1: 0,
			p2: 0,
			l1: 0,
			l2: 0,
		};

		const result = loads.reduce((result, load, idx) => {
			if (isEqual(load, emptyLoad)) {
				return result;
			}

			const spanId = getSpanId({ idx, loads, useConsole });
			const loadValues = [load.p1, load.p2, load.l1, load.l2];

			const match = result.find(({ loads }) => {
				return isEqual(loads, loadValues);
			});

			if (match) {
				match.targetSpans.push(spanId);
			} else {
				result.push({
					targetSpans: [spanId],
					loads: loadValues,
				});
			}

			return result;
		}, []);

		if (!result.length) {
			//placeholder
			return [
				{
					targetSpans: [],
					loads: [0, 0, 0, 0],
				},
			];
		} else {
			return result;
		}
	};
}
